import { useEffect, useRef } from 'react';

// ANX-055: обёртка над Cloudflare Turnstile. Подключает официальный
// `api.js` один раз глобально, рендерит виджет на `<div>` и сообщает
// токен наружу через `onToken`. Не рендерится, если `siteKey` пуст.
//
// Использование:
//   <Turnstile siteKey={key} onToken={(t) => setToken(t)} />

interface TurnstileGlobal {
  render(
    el: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'compact';
    },
  ): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    __turnstileScriptPromise?: Promise<void>;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileScriptPromise) return window.__turnstileScriptPromise;

  window.__turnstileScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(s);
  });
  return window.__turnstileScriptPromise;
}

interface Props {
  siteKey: string;
  onToken: (token: string | null) => void;
}

export function Turnstile({ siteKey, onToken }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey || !containerRef.current) return;

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'error-callback': () => onToken(null),
          'expired-callback': () => onToken(null),
          theme: 'auto',
        });
      })
      .catch(() => {
        // Загрузка script'а упала (CSP-блок, оффлайн, тест-env без сети) —
        // отдаём null, форма останется заблокированной submit'ом и
        // покажет общую ошибку при попытке отправки.
        if (!cancelled) onToken(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // widget мог уже быть размонтирован Cloudflare — игнорируем.
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
